import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigationType } from "react-router-dom";
import axios from "../axios/axiosUmbraco";
import { useTranslate } from "./TranslationText";
import { showFirst12Words, showFirst17Words } from "../helper/showFirstSixWords";
import ServiceCardItem from "./ServiceCardItem";
import SearchBox from "./SearchBox";
import SectionTitle from "./SectionTitle";
import {
  checkIsReturning,
  consumeReturnTicket
} from "../helper/pageFilterPersistence";

const fetch = process.env.REACT_APP_HOME_PAGE_SERVICES_lIST_ID;
const SRV_TICKET_KEY = "srv_return_from_details";
const SRV_SEARCH_KEY = "srv_searchKeyWord";
const SRV_TAB_KEY = "srv_activeTab";

function clearServicesSessionStorage() {
  sessionStorage.removeItem(SRV_SEARCH_KEY);
  sessionStorage.removeItem(SRV_TAB_KEY);
  sessionStorage.removeItem(SRV_TICKET_KEY);
}

export default function ServicesSection({ isHomePage = false }) {
  const { currentLanguage } = useSelector(
    (state) => state.studBookSlice
  );
  const { translate } = useTranslate();
  const navigationType = useNavigationType();
  const location = useLocation();
  const prevLocationKey = useRef(location.key);
  const isInitialMount = useRef(true);
  const wasReturningRef = useRef(false);

  if (!isHomePage && isInitialMount.current) {
    isInitialMount.current = false;
    const isReturning = checkIsReturning(SRV_TICKET_KEY, navigationType, "services");
    wasReturningRef.current = isReturning;
    if (!isReturning) {
      clearServicesSessionStorage();
    }
  }

  const [activeTab, setActiveTab] = useState(() => {
    if (!isHomePage) {
      const savedTab = sessionStorage.getItem(SRV_TAB_KEY);
      if (savedTab) return savedTab;
    }
    return 'Most Used Services';
  });

  const [searchKeyWord, setSearchKeyWord] = useState(() => {
    if (!isHomePage) {
      return sessionStorage.getItem(SRV_SEARCH_KEY) || '';
    }
    return '';
  });

  useEffect(() => {
    if (!isHomePage) {
      sessionStorage.setItem(SRV_SEARCH_KEY, searchKeyWord);
    }
  }, [searchKeyWord, isHomePage]);

  useEffect(() => {
    if (!isHomePage) {
      sessionStorage.setItem(SRV_TAB_KEY, activeTab);
    }
  }, [activeTab, isHomePage]);

  useEffect(() => {
    if (!isHomePage && wasReturningRef.current) {
      consumeReturnTicket(SRV_TICKET_KEY);
    }
  }, [isHomePage]);

  useEffect(() => {
    if (!isHomePage && prevLocationKey.current !== location.key) {
      prevLocationKey.current = location.key;
      if (navigationType === "PUSH") {
        clearServicesSessionStorage();
        setSearchKeyWord('');
        setActiveTab('Most Used Services');
      }
    }
  }, [location.key, navigationType, isHomePage]);

  const [services, setServices] = useState([]);
  const [total, setTotal] = useState(0);

  const [mostUsedServices, setMostUsedServices] = useState([]);
  const [registerationServices, setRegisterationServices] = useState([]);
  const [notarisationServices, setNotarisationServices] = useState([]);
  const [approvalServices, setApprovalServices] = useState([]);

  function getCurrentLanguageUmbraco() {
    const lang = localStorage.getItem("eahsLang") ?? "en";
    if (lang === "en") return 'en-US';
    return 'ar-AE';
  }

  useEffect(() => {
    async function getdata() {
      const umbraco = await axios.get(`/umbraco/delivery/api/v1/content?fetch=${fetch}&skip=0&take=50`, {
        headers: {
          "accept-language": getCurrentLanguageUmbraco(),
        }
      });
      const formattedData = (umbraco.data?.items || []).map((item) => ({
        id: item?.id ?? '',
        serviceName: showFirst12Words(item?.name) ?? '',
        category: item?.properties?.category ?? [],
        estimateTime: item?.properties?.estimateTime ?? "",
        feesAmount: item?.properties?.feesAmount ?? 0,
        feesDescription: item?.properties?.feesDescription ?? "",
        requiredDocuments: item?.properties?.requiredDocuments?.markup ?? '',
        serviceForm: item?.properties?.serviceForm ?? "",
        serviceImage: item.properties?.serviceImage ? item.properties?.serviceImage[0]?.url : '',
        subTitle: showFirst17Words(item.properties?.subTitle) ?? "",
        termsAndConditions: item?.properties?.termsAndConditions?.markup ?? '',
        serviceIcon: item.properties?.serviceIcon ? item.properties?.serviceIcon[0]?.url : ''
      }));
      setTotal(umbraco.data?.total || 0);
      setServices(formattedData);
      setMostUsedServices(formattedData.filter(service => service.category.includes("Most Used Services")));
      setRegisterationServices(formattedData.filter(service => service.category.includes("Registration Services")));
      setNotarisationServices(formattedData.filter(service => service.category.includes("Notarisation Services")));
      setApprovalServices(formattedData.filter(service => service.category.includes("Approval Services")));
    }
    getdata();
  }, [currentLanguage]);

  const filterdServices = services.filter(item => searchKeyWord.toLowerCase() === '' || item.serviceName.toLowerCase().includes(searchKeyWord.toLowerCase()));
  const tabsClasses = isHomePage ? "col-lg-3 col-md-4 col-sm-6 col-8 my-2 text-center px-0" : "col-xl-2-5 col-lg-2 col-md-4 col-sm-6 col-8 my-2 text-center px-0";

  return (
    <>
      {total > 0 &&
        <div className="second_services_section_container">
          {/* Tabs */}
          {!isHomePage &&
            <div className="mb-3">
              <SearchBox key={'servicesSearchBox'} value={searchKeyWord} onChange={(e) => {
                setActiveTab('All');
                setSearchKeyWord(e.target.value);
              }}
                onSubmitBtnFunction={() => {
                  setActiveTab('All');
                }}
              />
            </div>
          }
          <div className={isHomePage ? "container mobile-extra-padding pb-3" : 'pb-3 mobile-extra-padding'}>
            {isHomePage &&
              <SectionTitle section_id={"services"} isContainer={false} section_name={translate('web.ourServices', "our services")} />
            }
            <div className="row second_section_bar" style={{ cursor: 'pointer', justifyContent: isHomePage ? 'space-between' : 'flex-start' }}>
              {mostUsedServices.length > 0 &&
                <div
                  className={tabsClasses} style={{ minWidth: 250 }}
                  id="second_most_used_services"
                  onClick={() => setActiveTab('Most Used Services')}
                >
                  <span className={`${activeTab === 'Most Used Services' ? 'fw-bold' : ''} capatalize_text`} style={{ fontSize: 20 }}>{translate('web.mostUsedServices', 'Most Used Services')}</span>
                  <div className="service_bar_underline" style={activeTab === 'Most Used Services' ? { border: '3px solid #e44d52', marginTop: 16 } : {}}></div>
                </div>
              }
              {registerationServices.length > 0 &&
                <div
                  className={tabsClasses} style={{ minWidth: 250 }}
                  id="second_registration_services"
                  onClick={() => setActiveTab('Registration Services')}
                >
                  <span className={`${activeTab === 'Registration Services' ? 'fw-bold' : ''} capatalize_text`} style={{ fontSize: 20 }}>{translate('web.registrationServices', 'Registration Services')}</span>
                  <div className="service_bar_underline" style={activeTab === 'Registration Services' ? { border: '3px solid #e44d52', marginTop: 16 } : {}}></div>
                </div>
              }

              {notarisationServices.length > 0 &&
                <div
                  className={tabsClasses} style={{ minWidth: 250 }}
                  id="second_notarisation_services"
                  onClick={() => setActiveTab('Notarisation Services')}
                >
                  <span className={`${activeTab === 'Notarisation Services' ? 'fw-bold' : ''} capatalize_text`} style={{ fontSize: 20 }}>{translate('web.notarisationServices', 'notarisation Services')}</span>
                  <div className="service_bar_underline" style={activeTab === 'Notarisation Services' ? { border: '3px solid #e44d52', marginTop: 16 } : {}}></div>
                </div>
              }
              {approvalServices.length > 0 &&
                <div
                  className={tabsClasses} style={{ minWidth: 250 }}
                  id="second_approval_services"
                  onClick={() => setActiveTab('Approval Services')}
                >
                  <span className={`${activeTab === 'Approval Services' ? 'fw-bold' : ''} capatalize_text`} style={{ fontSize: 20 }}>{translate('web.approvalServices', 'Approval Services')}</span>
                  <div className="service_bar_underline" style={activeTab === 'Approval Services' ? { border: '3px solid #e44d52', marginTop: 16 } : {}}></div>
                </div>
              }
              {!isHomePage &&
                <div
                  className={tabsClasses} style={{ minWidth: 250 }}
                  id="second_approval_services_all"
                  onClick={() => setActiveTab('All')}
                >
                  <span className={`${activeTab === 'All' ? 'fw-bold' : ''} capatalize_text`} style={{ fontSize: 20 }}>{translate('web.all', 'All')}</span>
                  <div className="service_bar_underline" style={activeTab === 'All' ? { border: '3px solid #e44d52', marginTop: 16 } : {}}></div>
                </div>}
            </div>
          </div>
          {/* services Section */}
          <div className={isHomePage ? "container" : ''}>
            <div className="row rtl">
              {activeTab === 'Most Used Services' && mostUsedServices.length > 0 &&
                mostUsedServices.map((service, index) => (
                  <ServiceCardItem key={service.id || index} service={service} translate={translate} index={index} />
                ))}
              {activeTab === 'Registration Services' && registerationServices.length > 0 &&
                registerationServices.map((service, index) => (
                  <ServiceCardItem key={service.id || index} service={service} translate={translate} index={index} />
                ))}
              {activeTab === 'Notarisation Services' && notarisationServices.length > 0 &&
                notarisationServices.map((service, index) => (
                  <ServiceCardItem key={service.id || index} service={service} translate={translate} index={index} />
                ))}
              {activeTab === 'Approval Services' && approvalServices.length > 0 &&
                approvalServices.map((service, index) => (
                  <ServiceCardItem key={service.id || index} service={service} translate={translate} index={index} />
                ))}

              {activeTab === 'All' && !isHomePage && services.length > 0 &&
                filterdServices.map((service, index) => (
                  <ServiceCardItem key={service.id || index} service={service} translate={translate} index={index} />
                ))}
            </div>
          </div>
        </div>
      }
    </>
  );
}
